import { useState, useEffect } from 'react';

const CircularPercentage = ({
  percentage = 100,
  size = 200,
  trackColor = '#28a745',
  bgColor = '#f0f0f0',
  textColor = '#333333',
  strokeWidth = 3,
  animate = true,
  animationDuration = 1000,
  showGrid = false,
}) => {
  const [progress, setProgress] = useState(0);

  // Calculate the radius and center point
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;

  // Calculate the stroke dasharray and dashoffset for the progress
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  useEffect(() => {
    if (animate) {
      // Reset progress to 0 when percentage changes
      setProgress(0);

      // Animate the progress
      const timer = setTimeout(() => {
        setProgress(percentage);
      }, 50);

      return () => clearTimeout(timer);
    } else {
      setProgress(percentage);
    }
  }, [percentage, animate]);

  return (
    <div className="flex items-center justify-center">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Grid background if showGrid is true */}
        {showGrid && (
          <div
            className="absolute inset-0 bg-purple-50"
            style={{
              backgroundSize: '10px 10px',
              backgroundImage:
                'linear-gradient(to right, #e0e0e0 1px, transparent 1px), linear-gradient(to bottom, #e0e0e0 1px, transparent 1px)',
              zIndex: -1,
            }}
          />
        )}

        {/* Background circle */}
        <svg className="w-full h-full" viewBox={`0 0 ${size} ${size}`}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="transparent"
            stroke={bgColor}
            strokeWidth={strokeWidth}
          />

          {/* Progress circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="transparent"
            stroke={trackColor}
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{
              transition: animate ? `stroke-dashoffset ${animationDuration}ms ease-in-out` : 'none',
              transform: 'rotate(-90deg)',
              transformOrigin: 'center',
            }}
          />

          {/* Percentage text */}
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={textColor}
            fontSize={`${size / 5}px`}
            fontWeight="bold"
          >
            {Math.round(progress)}%
          </text>
        </svg>
      </div>
    </div>
  );
};

// Example usage
const ProgressBar = ({ value }: { value: number }) => {
  return (
    <div className="flex flex-col items-center gap-8">
      <CircularPercentage percentage={value} size={30} trackColor="#28a745" showGrid={true} />
    </div>
  );
};

export default ProgressBar;
